import {
  IPublicClientApplication,
  InteractionRequiredAuthError,
  BrowserAuthError,
} from '@azure/msal-browser';
import { loginRequest } from '@/services/authConfig';

let _msalInstance: IPublicClientApplication | null = null;

/**
 * Call this once on app startup to register the MSAL instance.
 * Account is always read fresh from the instance at request time.
 */
export function initAuthFetch(instance: IPublicClientApplication) {
  _msalInstance = instance;
}

/**
 * Waits up to maxWaitMs for the MSAL instance to be initialised.
 * Handles the race condition where a request fires before AuthInit runs.
 */
async function waitForMsal(maxWaitMs = 5000): Promise<IPublicClientApplication> {
  if (_msalInstance) return _msalInstance;

  const interval = 100;
  const attempts = maxWaitMs / interval;

  for (let i = 0; i < attempts; i++) {
    await new Promise(r => setTimeout(r, interval));
    if (_msalInstance) return _msalInstance;
  }

  throw new Error('Authentication not ready — please refresh the app and try again');
}

/**
 * Drop-in replacement for fetch() that automatically attaches a Bearer token.
 * Throws a descriptive error rather than sending a tokenless request,
 * so callers can surface a meaningful message to the user.
 */
export async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  // In dev/mock mode, skip token acquisition entirely
  if (import.meta.env.DEV || import.meta.env.VITE_MOCK_AUTH === 'true') {
    return fetch(url, options);
  }

  // Wait for MSAL to initialise — handles race condition on app startup
  let instance: IPublicClientApplication;
  try {
    instance = await waitForMsal();
  } catch (initErr: any) {
    throw new Error(initErr.message);
  }

  // Always read the active account fresh
  const accounts = instance.getAllAccounts();
  const account = accounts[0] ?? null;

  if (!account) {
    console.warn('[authFetch] No MSAL account — redirecting to login');
    try {
      await instance.loginRedirect(loginRequest);
    } catch {
      // loginRedirect throws if interaction is already in progress
    }
    throw new Error('Session expired — please log in again');
  }

  let accessToken: string | null = null;

  // 1. Try silent acquisition (uses cached/refreshed token)
  try {
    const tokenResponse = await instance.acquireTokenSilent({
      ...loginRequest,
      account,
    });
    accessToken = tokenResponse.accessToken;
  } catch (silentErr) {
    console.warn('[authFetch] Silent token acquisition failed:', silentErr);

    const needsInteraction =
      silentErr instanceof InteractionRequiredAuthError ||
      silentErr instanceof BrowserAuthError;

    if (needsInteraction) {
      // 2. Try popup (works in browser tabs)
      try {
        const tokenResponse = await instance.acquireTokenPopup({
          ...loginRequest,
          account,
        });
        accessToken = tokenResponse.accessToken;
      } catch (popupErr: any) {
        console.warn('[authFetch] Popup failed:', popupErr?.message);

        // 3. Popup blocked (PWA, iOS Safari) — redirect to login
        const isPopupUnavailable =
          popupErr?.message?.includes('popup') ||
          popupErr?.message?.includes('interaction_in_progress') ||
          popupErr instanceof BrowserAuthError;

        if (isPopupUnavailable) {
          console.warn('[authFetch] Redirecting to login — popup unavailable');
          try {
            await instance.loginRedirect(loginRequest);
          } catch {
            // Redirect may throw if interaction already in progress
          }
          throw new Error('Session expired — please log in again');
        }

        // Non-popup error — surface it
        throw new Error(`Authentication failed: ${popupErr?.message ?? 'unknown error'}`);
      }
    } else {
      // Non-interaction error (e.g. network down during token refresh)
      throw new Error(`Could not refresh session: ${(silentErr as any)?.message ?? 'unknown error'}`);
    }
  }

  if (!accessToken) {
    // All paths exhausted — throw rather than sending a tokenless request
    throw new Error('Could not obtain authentication token — please log out and log in again');
  }

  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${accessToken}`,
    },
  });
}